/*************************************************************************
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: Adobe permits you to use, modify, and distribute this file in
 * accordance with the terms of the Adobe license agreement accompanying
 * it. If you have received this file from a source other than Adobe,
 * then your use, modification, or distribution of it requires the prior
 * written permission of Adobe.
 **************************************************************************/

/**
 * APIs that simplify code to make authenticated network requests for resources, execute GraphQL
 * queries, etc. and optimize them through efficient batching when appropriate.
 *
 * ***Import:***
 *
 * ```typescript
 * import {batch, fetch, query} from '@adobe/exc-app/network';
 * ```
 *
 * ***Default export:***
 *
 * [NetworkApi](../interfaces/network.networkapi.md)
 *
 * ***Usage:***
 *
 * ```typescript
 * import {batch, fetch, query} from '@adobe/exc-app/network';
 *
 * // Performs a window.fetch call with Authorization and x-api-key headers set
 * const fetchResponse = await fetch('https://localhost', {auth: 'Header', method: 'GET'});
 *
 * // Executes a query for resources to the ExC GraphQL service
 * const queryResponse = await query({
 *   data: {
 *     query: `
 *       query userBehanceQuery($userId: String!, $apiKey: String!) {
 *         userBehance(userId: $userId, apiKey: $apiKey) {
 *           images
 *         }
 *       }`,
 *     variables: {
 *       apiKey: 'test-app',
 *       userId: '123@AdobeID'
 *     }
 *   },
 *   operationName: 'BehanceAvatar'
 * });
 *
 * // Calls made that get batched into a single network call
 * const [batchResponse1, batchResponse2] = await Promise.all([
 *   batch('https://localhost/api1', {auth: 'Header', priority: 'P1'}),
 *   batch('https://example.com/api2', {auth: 'Query', priority: 'P1'})
 * ]);
 * ```
 * @packageDocumentation
 * @module network
 */

import {getImpl} from './src/Global';

interface BatchOptions {
  /**
   * A value indicating the desired batch debouncing priority level.
   */
  priority?: BatchPriority;
}

interface FetchOptions {
  /**
   * A boolean value indicating whether to add Authentication token, API Key to the request.
   */
  auth?: 'Header' | 'Query' | 'Body';
  metadata?: DefaultMetaData;
}

/**
 * User Sandbox Interface
 */
interface Sandbox {
  /**
   * Whether it is the default sandbox
   */
  isDefault: boolean;
  /**
   * Sandbox ID
   */
  name: string;
  /**
   * Region
   */
  region: string;
  /**
   * Current state
   */
  state: string;
  /**
   * Sandbox Display Title
   */
  title: string;
  /**
   * Environment type
   */
  type: string;
}

/**
 * @ignore
 */
export interface Configuration {
  /**
   * Unified Shell API Gateway URL
   */
  apiGatewayUrl: string;
  /**
   * appId from solution route config
   */
  appId: string;
  /**
   * Get Authentication token
   */
  getTokenInfo: () => Promise<{token: string}>;
  /**
   * IMS Client ID
   */
  imsClientId: string;
  /**
   * metricsAppId from solution route config
   */
  metricsAppId?: string;
  /**
   * @deprecated tenantAppId from solution route config
   */
  tenantAppId?: string;
  /**
   * IMS org id
   */
  imsOrg?: string;
  /**
   * Current sandbox object
   */
  sandbox?: Sandbox;
}

/**
 * GQL Query containing graphql query and variables.
 */
export interface GraphQLQuery {
  /**
   * GQL Query.
   *
   * ***Example:***
   *
   * ```typescript
   * {query: `
   *   query userBehanceQuery($userId: String!, $apiKey: String!) {
   *     userBehance(userId: $userId, apiKey: $apiKey) {
   *       images
   *     }
   *   }`
   * };
   *
   * {query: `
   *   query userBehanceQuery {
   *     userBehance(userId: "123@AdobeID", apiKey: "test-app") {
   *       images
   *     }
   *   }`
   * };
   * ```
   */
  query: string;
  /**
   * Query specific variables- key value pairs.
   *
   * ***Example:***
   *
   * ```typescript
   * {variables: {
   *   apiKey: 'test-app',
   *   userId: '123@AdobeID'
   *  }
   * };
   * ```
   */
  variables?: Record<string, any>;
}
/**
 * ENUM which module developers can use for giving the preferred region.
 */
export enum ServiceDCRegions {
  azureEastUS = 'va7',
  azureWestUS = 'va7',
  azureCentralUS = 'va7',
  azureWestEurope = 'nld2',
  azureCentralEurope = 'nld2',
  azureEastEurope = 'nld2',
  azureEastAustralia = 'aus5',
  azureWestAustralia = 'aus5',
  azureCentralAustralia = 'aus5',
  azureSouthEastAsia = 'aus5',
  azureEastChina = 'aus5',
  azureCentralIndia = 'aus5',
  azureEastJapan = 'aus5'
}

export interface DefaultMetaData {
  imsClientId? : string;
  token? : string;
  imsOrg? : string;
  sandbox?: Record<string, any>;
}

/**
 * Query request interface.
 */
export interface QueryRequest {
  /**
   * Overwrite config.appId until federation is out. This allow scenario where sharing components that are linked to their own tenants.
   */
  appId?: string;
  /**
   * Data containing single or multiple GQL queries.
   *
   * ***Example:***
   *
   * ```typescript
   * {data: {
   *   query: `
   *     query userBehanceQuery($userId: String!, $apiKey: String!) {
   *       userBehance(userId: $userId, apiKey: $apiKey) {
   *         images
   *       }
   *     }`,
   *   variables: {
   *     userId: '123@AdobeID',
   *     apiKey: 'test-app'
   *   }
   *  }
   * };
   * ```
   */
  data: GraphQLQuery | Array<GraphQLQuery>;
  /**
   * Query ID- To analyze a query's metrics & performance.
   *
   * ***Example:***
   *
   * ```typescript
   * {operationName: 'BehanceAvatar'}
   * ```
   */
  operationName?: string;
  /**
   * Preferred Region should be used only for legacy apps who don't have data replication in place.
   */
  preferredRegion?: ServiceDCRegions;
  /**
   * default metadata should be given when you want to override the default metadata which will be passed with every request
   */
  metadata?: DefaultMetaData;
}

/**
 * Defines the object containing any custom settings that you want to apply to the request. You can
 * also additionally specify the 'auth' parameter to automatically set the Authentication, API key in Headers/Query params.
 *
 * ***Example:***
 *
 * `{auth: 'Header', body: 'xyz', headers: {'Content-Type': 'text/plain'}, method: 'POST'}` or
 * `{auth: 'Query', method: 'GET'}` or
 * `{method: 'GET'}`
 */
export type FetchInit = RequestInit & FetchOptions;

/**
 * Defines the object containing any custom settings that you want to apply to the request. You can
 * also additionally specify the 'auth' parameter to automatically set the Authentication, API key in Headers/Query params
 * and specify the 'priority' parameter to set the batch debouncing priority.
 *
 * ***Example:***
 *
 * `{auth: 'Header', body: 'xyz', headers: {'Content-Type': 'text/plain'}, method: 'POST', priority: 'P1'}` or
 * `{auth: 'Query', method: 'GET', priority: 'P2'}` or
 * `{method: 'GET', priority: 'P3'}` or
 * `{method: 'DELETE'}`
 */
export type BatchInit = RequestInit & FetchOptions & BatchOptions;

/**
 * Defines the batch debouncing priority level.
 *
 * ***Example:***
 *
 * `P1` or `P2` or `P3`
 */
export type BatchPriority = 'P1' | 'P2' | 'P3';

export interface NetworkApi {
  /**
   * @ignore
   */
  configure(config: Configuration): void;

  /**
   * Provides an interface for fetching resources powered by the global 'fetch' API.
   *
   * ***Example:***
   *
   * ```typescript
   * // performs a window.fetch call
   * let response = await fetch('https://example.com/api/ping');
   *
   * // performs a window.fetch call with Authorization and x-api-key headers set
   * response = await fetch('https://localhost', {auth: 'Header', method: 'GET'});
   *
   * // performs a window.fetch call with user_token and client_id query parameters added to the URL
   * const request = new Request('https://localhost', {
   *   body: JSON.stringify({k: 'v'}),
   *   headers: new Headers(),
   *   method: 'POST'
   * });
   * response = await fetch(request, {auth: 'Query'});
   * ```
   * @param input The resource that you wish to fetch. It can either be the URL of the resource
   * you want to fetch or a Request object.
   * @param init An object containing any custom settings that you want to apply to the request.
   * @returns The promise for the response to the fetch operation.
   */
  fetch(input: RequestInfo, init?: FetchInit): Promise<Response>;

  /**
   * Provides an interface for querying resources via GraphqQL.
   * In order to consume query, please make sure the respective query resolver is
   * available in the GraphQL Service.
   *
   * ***Example:***
   *
   * ```typescript
   * const BEHANCE_QUERY = `
   *   query userBehanceQuery($userId: String!, $apiKey: String!) {
   *     userBehance(userId: $userId, apiKey: $apiKey) {
   *       images
   *     }
   *   }`;
   *
   * // queries the respective resource via GraphQL and returns HTTP Response {ok: true, status: 200, ...}
   * query({data: {query: BEHANCE_QUERY, variables: {
   *   userId: '123@AdobeID',
   *   apiKey: 'test-app',
   * }}, operationName: 'BehanceAvatar'});
   *
   * // queries the respective resource via GraphQL and returns HTTP Response {ok: true, status: 200, ...}
   * query({data: {query: `
   *   query userBehanceQuery {
   *     userBehance(userId: "123@AdobeID", apiKey: "test-app") {
   *       images
   *     }
   *   }`
   * }});
   * ```
   * @param request Query request containing desired GQL Query.
   * @returns The promise for the response to the query operation.
   */
  query(request: QueryRequest): Promise<Response>;

  /**
   * Fetches a resource by batching it with other calls to batch made with the same `priority` at
   * around the same time. For example, below two API calls would get combined into a single network
   * call.
   *
   * This is achieved by converting multiple calls to the `batch` API into a GraphQL query that the
   * ExC GraphQL service has a built-in resolver for.
   *
   * Supported methods: GET, POST, PATCH, DELETE.
   *
   * ***Example:***
   *
   * ```typescript
   * // Batching two resource fetches into a single network call
   * const [batchResponse1, batchResponse2] = await Promise.all([
   *   batch('https://localhost/api1', {auth: 'Header', priority: 'P1'}),
   *   batch('https://example.com/api2', {auth: 'Query', priority: 'P1'})
   * ]);
   *
   * const request = new Request('https://localhost', {
   *   body: JSON.stringify({k: 'v'}),
   *   headers: new Headers(),
   *   method: 'POST'
   * });
   *
   * // batches HTTP resources with P2(default) priority debouncing level and without Authentication token, API Key
   * // and returns HTTP Response {ok: true, status: 200, ...}.
   * batch(request);
   *
   * // batches HTTP resources with P3(lowest) priority debouncing level and Authentication token, API Key in Query params
   * // and returns HTTP Response {ok: true, status: 200, ...}.
   * batch(request, {auth: 'Query', priority: 'P3'});
   * ```
   * @param input The resource that you wish to fetch. It can either be the URL of the resource you want to fetch or a Request object.
   * @param init An object containing any custom settings that you want to apply to the request.
   * @returns The promise for the response to the batch operation.
   */
  batch(input: RequestInfo, init?: BatchInit): Promise<Response>;
}

/**
 * Provides an interface for fetching resources powered by the global 'fetch' API.
 *
 * ***Example:***
 *
 * ```typescript
 * // performs a window.fetch call
 * let response = await fetch('https://example.com/api/ping');
 *
 * // performs a window.fetch call with Authorization and x-api-key headers set
 * response = await fetch('https://localhost', {auth: 'Header', method: 'GET'});
 *
 * // performs a window.fetch call with user_token and client_id query parameters added to the URL
 * const request = new Request('https://localhost', {
 *   body: JSON.stringify({k: 'v'}),
 *   headers: new Headers(),
 *   method: 'POST'
 * });
 * response = await fetch(request, {auth: 'Query'});
 * ```
 * @param input The resource that you wish to fetch. It can either be the URL of the resource you
 * want to fetch or a Request object.
 * @param init An object containing any custom settings that you want to apply to the request.
 * @returns The promise for the response to the fetch operation.
 */
export function fetch(input: RequestInfo, init?: FetchInit): Promise<Response> {
  return getImpl('network').fetch(input, init);
}

/**
 * Provides an interface for querying resources via GraphqQL.
 * In order to consume query, please make sure the respective query resolver is
 * available in the GraphQL Service.
 * ***Example:***
 *
 * ```typescript
 * const BEHANCE_QUERY = `
 *   query userBehanceQuery($userId: String!, $apiKey: String!) {
 *     userBehance(userId: $userId, apiKey: $apiKey) {
 *       images
 *     }
 *   }`;
 *
 * // queries the respective resource via GraphQL and returns HTTP Response {ok: true, status: 200, ...}
 * query({data: {query: BEHANCE_QUERY, variables: {
 *   userId: '123@AdobeID',
 *   apiKey: 'test-app',
 * }}, operationName: 'BehanceAvatar'});
 *
 * // queries the respective resource via GraphQL and returns HTTP Response {ok: true, status: 200, ...}
 * query({data: {query: `
 *   query userBehanceQuery {
 *     userBehance(userId: "123@AdobeID", apiKey: "test-app") {
 *       images
 *     }
 *   }`
 * }});
 * ```
 * @param input Query request containing desired GQL Query.
 * @returns The promise for the response to the query operation.
 */
export function query(input: QueryRequest): Promise<Response> {
  return getImpl('network').query(input);
}

/**
 * Fetches a resource by batching it with other calls to batch made with the same `priority` at
 * around the same time. For example, below two API calls would get combined into a single network
 * call.
 *
 * This is achieved by converting multiple calls to the `batch` API into a GraphQL query that the
 * ExC GraphQL service has a built-in resolver for.
 *
 * ***Example:***
 *
 * ```typescript
 * // Batching two resource fetches into a single network call
 * const [batchResponse1, batchResponse2] = await Promise.all([
 *   batch('https://localhost/api1', {auth: 'Header', priority: 'P1'}),
 *   batch('https://example.com/api2', {auth: 'Query', priority: 'P1'})
 * ]);
 *
 * const request = new Request('https://localhost', {
 *   body: JSON.stringify({k: 'v'}),
 *   headers: new Headers(),
 *   method: 'POST'
 * });
 *
 * // batches HTTP resources with P2(default) priority debouncing level and without Authentication token, API Key
 * // and returns HTTP Response {ok: true, status: 200, ...}.
 * batch(request);
 *
 * // batches HTTP resources with P3(lowest) priority debouncing level and Authentication token, API Key in Query params
 * // and returns HTTP Response {ok: true, status: 200, ...}.
 * batch(request, {auth: 'Query', priority: 'P3'});
 * ```
 * @param input The resource that you wish to fetch. It can either be the URL of the resource you want to fetch or a Request object.
 * @param init An object containing any custom settings that you want to apply to the request.
 * @returns The promise for the response to the batch operation.
 */
export function batch(input: RequestInfo, init?: BatchInit): Promise<Response> {
  return getImpl('network').batch(input, init);
}

/**
 * @ignore
 */
export class Internal {
  public static configure(config: Configuration): void {
    return getImpl('network').configure(config);
  }
}
